import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { CalendarDays, Columns3, List, Plus } from 'lucide-react';
import { Card, CardBody } from '@/components/ui/Card';
import { SelectorDeVista, type OpcionDeVista } from '@/components/ui/SelectorDeVista';
import { Spinner } from '@/components/ui/Spinner';
import { usePreferencia } from '@/hooks/usePreferencia';
import { useToast } from '@/hooks/useToast';
import { useDeshacer } from '@/hooks/deshacer-contexto';
import { useVeRuta } from '@/hooks/useMenu';
import { Superfiltro } from '@/components/datos/Superfiltro';
import { useFiltrosEnUrl } from '@/hooks/useFiltrosEnUrl';
import { opcionesDe, sinFiltros, textoDeResultados, type Faceta } from '@/lib/superfiltro';
import { VISTAS_DE_ME_TOCA, alInstante, deshacerDe, porDia, porFlujo, type VistaDeMeToca } from '@/lib/metoca';
import { unaDe } from '@/lib/preferencias';
import * as asuntosApi from '@/api/asuntos.api';
import type { Asunto, Cajon } from '@/api/asuntos.api';
import { HojaDelAsunto } from '@/components/athena/HojaDelAsunto';
import { NuevoAsunto } from '@/components/athena/NuevoAsunto';
import { TarjetaDeAsunto } from '@/components/athena/TarjetaDeAsunto';
import { VistaPorDia, VistaTablero, type AccionesDeAsunto } from '@/components/metoca/VistasDeMeToca';

/**
 * ME TOCA — la bandeja única.
 *
 * Mandato: *"Donde fusionamos lo que hoy son Mi día + Inspector. Esas dos
 * pantallas desaparecen."*
 *
 * Y desaparecen de verdad, no se esconden del menú: la misma tarea vivía en
 * dos pantallas con dos diseños, y dos vistas del mismo dato terminan siempre
 * contradiciéndose. Todo sale de `/api/asuntos`, igual que Hoy y Agenda.
 *
 * ═══ "RUIDO" SE PUEDE MIRAR ═══
 *
 * Lo que Athena descartó sola sigue estando, en su propio chip, y se rescata
 * con un toque. Esconderlo del todo sería pedirle al dueño que confíe en un
 * filtro que no puede auditar, cuando lo que se le filtra es plata.
 *
 * ═══ TRES VISTAS, LA MISMA LISTA (tanda 6) ═══
 *
 * Kervens: *"necesito ver tres tipos de vistas para que el cliente escoja la
 * que es más amigable para él"*. Lista (la de siempre), por día y tablero. Son
 * tres maneras de mirar los MISMOS asuntos con los mismos chips: cómo se
 * reparten vive en `lib/metoca`, y la elección sigue a la persona de la laptop
 * al teléfono (preferencia `metoca.vista`).
 */

type Chip = {
  id: string;
  etiqueta: string;
  filtro: (a: Asunto) => boolean;
  /** Los que piden datos que la lista normal no trae. */
  pide?: Partial<asuntosApi.FiltroDeAsuntos>;
};

const CHIPS: Chip[] = [
  {
    id: 'por-aprobar',
    etiqueta: 'Por aprobar',
    filtro: (a) => a.estado === 'DETECTADO',
  },
  {
    id: 'yo-prometi',
    etiqueta: 'Yo prometí',
    filtro: (a) => !a.responsable && ['ENVIAR_INFO', 'COTIZACION', 'ESCRIBIR'].includes(a.tipo),
  },
  {
    id: 'me-prometieron',
    etiqueta: 'Me prometieron',
    filtro: (a) => a.cajon === 'DINERO' || a.tipo === 'SEGUIMIENTO',
  },
  {
    id: 'delegado',
    etiqueta: 'Delegado',
    filtro: (a) => Boolean(a.responsable),
  },
  {
    id: 'ruido',
    etiqueta: 'Ruido',
    filtro: (a) => a.prioridad === 'RUIDO' || a.estado === 'DESCARTADO',
    pide: { incluirRuido: true, estado: 'todos' },
  },
];

const CAJONES: Array<{ valor: Cajon; etiqueta: string }> = [
  { valor: 'TRABAJO', etiqueta: 'Trabajo' },
  { valor: 'DINERO', etiqueta: 'Dinero' },
  { valor: 'CLIENTES', etiqueta: 'Clientes' },
  { valor: 'PERSONAL', etiqueta: 'Personal' },
];

const VISTAS: OpcionDeVista<VistaDeMeToca>[] = [
  { id: 'lista', etiqueta: 'Lista', icono: List, ayuda: 'Todo seguido, lo más urgente arriba' },
  { id: 'dias', etiqueta: 'Por día', icono: CalendarDays, ayuda: 'Por cuándo toca: vencido, hoy, mañana…' },
  { id: 'tablero', etiqueta: 'Tablero', icono: Columns3, ayuda: 'En qué va cada cosa: por aprobar, por hacer, esperando y vencido' },
];
const sanearVista = unaDe(VISTAS_DE_ME_TOCA, 'lista');

type AccionRapida = 'aprobar' | 'hecho' | 'descartar' | 'rescatar';
type Respuesta = Awaited<ReturnType<typeof asuntosApi.getAsuntos>>;

export default function MeToca() {
  // SUPERFILTRO (Josué, 14-oct): los chips de antes son la faceta «Qué»; se suman cajón y responsable.
  const { filtros, ponerVarios } = useFiltrosEnUrl(
    { chip: '', cajon: '', responsable: '', buscar: '' },
    { chip: CHIPS.map((c) => c.id), cajon: CAJONES.map((c) => c.valor) },
  );
  const [abierto, setAbierto] = useState<string | null>(null);
  const [anotando, setAnotando] = useState(false);
  const [vista, setVista] = usePreferencia<VistaDeMeToca>('metoca.vista', 'lista', sanearVista);
  const navegar = useNavigate();
  const toast = useToast();
  const { ofrecerDeshacer } = useDeshacer();
  const qc = useQueryClient();
  // Un mensaje para el cliente se revisa en Seguimientos, si quien mira tiene esa pantalla.
  const veSeguimientos = useVeRuta('/seguimientos');

  const chipActivo = filtros.chip || 'todos';
  const cajon = (filtros.cajon || undefined) as Cajon | undefined;
  const chip = CHIPS.find((c) => c.id === chipActivo);
  const llave = ['asuntos', 'me-toca', chipActivo, cajon];

  const asuntos = useQuery({
    queryKey: llave,
    queryFn: () => asuntosApi.getAsuntos({ limite: 300, cajon, ...(chip?.pide ?? {}) }),
  });

  const posponer = useMutation({
    mutationFn: ({ id, hasta }: { id: string; hasta: string }) =>
      asuntosApi.moverAsunto(id, 'posponer', { hasta }),
    onSuccess: () => {
      toast.success('Pospuesto');
      void qc.invalidateQueries({ queryKey: ['asuntos'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const mover = useMutation({
    mutationFn: ({ id, accion }: { id: string; accion: AccionRapida }) =>
      asuntosApi.moverAsunto(id, accion),
    // La pantalla cambia en el acto (`alInstante`): una tarjeta soltada en «Por
    // hacer» no puede quedarse en «Por aprobar» esperando al servidor. Si el
    // servidor no quiere, vuelve la lista de antes y se dice por qué.
    onMutate: async ({ id, accion }) => {
      await qc.cancelQueries({ queryKey: llave });
      const antes = qc.getQueryData<Respuesta>(llave);
      if (antes) qc.setQueryData<Respuesta>(llave, { ...antes, asuntos: alInstante(antes.asuntos, id, accion) });
      // Cómo estaba antes de tocarlo: de eso depende con qué se deshace (`deshacerDe`).
      return { antes, llave, estadoAnterior: antes?.asuntos.find((a) => a.id === id)?.estado };
    },
    onError: (e: Error, _v, contexto) => {
      if (contexto?.antes) qc.setQueryData(contexto.llave, contexto.antes);
      toast.error(e.message);
    },
    onSuccess: (_d, v, contexto) => {
      const dicho = { aprobar: 'Aprobado', hecho: 'Hecho', descartar: 'Descartado', rescatar: 'Rescatado' };
      // Deshacer en vez de un aviso: con la red puesta, deslizar sin mirar es
      // barato, y deslizar sin mirar es lo que se hace con la lista del dia.
      //
      // Deshacer es volver a como estaba, nunca «aprobar» lo que nadie aprobó
      // (`deshacerDe`). Donde no hay vuelta exacta, o no se sabe cómo estaba,
      // el aviso va sin botón: un deshacer que a veces restaura mal es peor
      // que no tenerlo.
      const vuelta = contexto?.estadoAnterior ? deshacerDe(v.accion, contexto.estadoAnterior) : null;
      if (!vuelta) {
        toast.success(dicho[v.accion]);
        return;
      }
      ofrecerDeshacer({
        texto: dicho[v.accion],
        deshacer: async () => {
          await asuntosApi.moverAsunto(v.id, vuelta);
          void qc.invalidateQueries({ queryKey: ['asuntos'] });
          toast.success('Deshecho');
        },
      });
    },
    onSettled: () => void qc.invalidateQueries({ queryKey: ['asuntos'] }),
  });

  const todos: Asunto[] = asuntos.data?.asuntos ?? [];
  const q = filtros.buscar.trim().toLowerCase();
  const lista = (chip ? todos.filter(chip.filtro) : todos).filter(
    (a) =>
      (!filtros.responsable || a.responsable?.nombre === filtros.responsable) &&
      (!q || `${a.descripcion} ${a.conQuien} ${a.codigo}`.toLowerCase().includes(q)),
  );
  const esRuido = chipActivo === 'ruido';
  const facetas: Faceta[] = [
    { clave: 'chip', titulo: 'Qué', opciones: CHIPS.map((c) => ({ valor: c.id, etiqueta: c.etiqueta })) },
    { clave: 'cajon', titulo: 'Cajón', opciones: CAJONES },
    { clave: 'responsable', titulo: 'Responsable', opciones: opcionesDe(todos, (a) => a.responsable?.nombre) },
  ];

  // Lo mismo en las tres vistas. El botón principal manda lo que diga la
  // tarjeta («aprobar» una suposición, «hecho» lo ya confirmado); en «Ruido»
  // rescata, y lo dice. Un mensaje para el cliente no se mueve desde aquí:
  // «revisar» es ir a Seguimientos a leerlo (`onRevisar`).
  const acciones: AccionesDeAsunto = {
    onAbrir: setAbierto,
    onPrincipal: esRuido
      ? (id) => mover.mutate({ id, accion: 'rescatar' })
      : (id, accion) => {
          if (accion !== 'revisar') mover.mutate({ id, accion });
        },
    rotuloPrincipal: esRuido ? 'Rescatar' : undefined,
    onRevisar: veSeguimientos ? () => navegar('/seguimientos') : undefined,
    onDescartar: esRuido ? undefined : (id) => mover.mutate({ id, accion: 'descartar' }),
    onPosponer: (id, hasta) => posponer.mutate({ id, hasta }),
    onVerMensaje: (x) =>
      navegar(`/chats/${x.conversationId}${x.mensajeId ? `?mensaje=${x.mensajeId}` : ''}`),
  };

  const vacio = esRuido
    ? { titulo: 'Athena no ha descartado nada todavía.' }
    : chip
      ? { titulo: `Nada en "${chip.etiqueta}".` }
      : { titulo: 'No tienes nada abierto.', detalle: 'Cuando Athena lea tus conversaciones, aparece aquí.' };

  // Un solo «ahora» para toda la pantalla: a medianoche, dos lecturas darían dos días distintos.
  const ahora = new Date();
  const columnas = vista === 'tablero' ? porFlujo(lista, ahora) : [];

  return (
    <div className="space-y-3">
      <div className="space-y-1">
        <div className="flex items-center justify-between gap-3">
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-50">Me toca</h1>
          <div className="flex shrink-0 items-center gap-2">
            <SelectorDeVista opciones={VISTAS} valor={vista} onCambiar={setVista} etiqueta="Cómo ver lo que te toca" />
            <button
              onClick={() => setAnotando(true)}
              className={
                'grid h-11 w-11 shrink-0 place-items-center rounded-lg bg-brand-600 text-white transition-colors hover:bg-brand-700 md:h-10 md:w-10 ' +
                'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600'
              }
              aria-label="Anotar algo"
              title="Anotar algo"
            >
              <Plus className="h-5 w-5" />
            </button>
          </div>
        </div>
        <p className="text-sm text-slate-600 dark:text-slate-300">
          Todo lo que Athena sacó de tus conversaciones.
        </p>
      </div>

      <Superfiltro
        buscar={filtros.buscar}
        onBuscar={(v) => ponerVarios({ buscar: v })}
        placeholder="Buscar por asunto o persona…"
        facetas={facetas}
        valores={filtros}
        onCambiar={ponerVarios}
        onLimpiar={() => ponerVarios(sinFiltros(facetas))}
        resultados={asuntos.isPending ? undefined : textoDeResultados(lista.length, todos.length, ['asunto', 'asuntos'])}
      />

      {esRuido && (
        <p className="rounded-lg bg-slate-100 p-2.5 text-xs text-slate-600 dark:bg-slate-800 dark:text-slate-300">
          Esto es lo que Athena descartó sola. Si se equivocó, rescátalo: nada se borra.
        </p>
      )}

      {asuntos.isPending ? (
        <Card>
          <CardBody>
            <Spinner />
          </CardBody>
        </Card>
      ) : asuntos.isError ? (
        <Card>
          <CardBody>
            <p className="py-6 text-center text-sm text-red-600 dark:text-red-300">
              No se pudieron leer tus asuntos.
            </p>
          </CardBody>
        </Card>
      ) : (
        // Cambiar de vista entra con un fundido.
        <div key={vista} className="motion-safe:animate-fade-in">
          {vista === 'tablero' ? (
            <VistaTablero
              columnas={columnas}
              fuera={lista.length - columnas.reduce((n, c) => n + c.asuntos.length, 0)}
              onVerLista={() => setVista('lista')}
              {...acciones}
            />
          ) : vista === 'dias' ? (
            <VistaPorDia grupos={porDia(lista, ahora)} ahora={ahora} vacio={vacio} {...acciones} />
          ) : (
            // En escritorio la lista va a dos columnas: a 1440 px una sola deja
            // media pantalla vacia y obliga a bajar el doble. La hoja del asunto
            // sigue abriendo como panel derecho encima.
            <Card>
              <CardBody className="space-y-0 xl:columns-2 xl:gap-6 [&>*]:break-inside-avoid">
                {lista.length === 0 ? (
                  <p className="py-10 text-center text-sm text-slate-400">
                    {[vacio.titulo, vacio.detalle].filter(Boolean).join(' ')}
                  </p>
                ) : (
                  lista.map((a) => <TarjetaDeAsunto key={a.id} asunto={a} conFecha {...acciones} />)
                )}
              </CardBody>
            </Card>
          )}
        </div>
      )}

      <NuevoAsunto abierto={anotando} onCerrar={() => setAnotando(false)} />
      <HojaDelAsunto asuntoId={abierto} onCerrar={() => setAbierto(null)} />
    </div>
  );
}
